import { Droplet, Check, type LucideIcon } from 'lucide-react';
import {
  Briefcase,
  Building2,
  Hammer,
  Wrench,
  Award,
  Leaf,
  Zap,
  Shield,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import type { WatermarkPosition } from '../types';

// Watermark preset icons (business/industry themed - same as logo icons)
const WATERMARK_ICONS: { id: string; icon: LucideIcon; name: string }[] = [
  { id: 'briefcase', icon: Briefcase, name: 'Business' },
  { id: 'building', icon: Building2, name: 'Corporate' },
  { id: 'hammer', icon: Hammer, name: 'Construction' },
  { id: 'wrench', icon: Wrench, name: 'Services' },
  { id: 'award', icon: Award, name: 'Premium' },
  { id: 'leaf', icon: Leaf, name: 'Eco' },
  { id: 'zap', icon: Zap, name: 'Energy' },
  { id: 'shield', icon: Shield, name: 'Security' },
];

// Helper to get icon component from watermark content
const getWatermarkIcon = (content: string | null): LucideIcon | null => {
  if (!content?.startsWith('icon:')) return null;
  const iconId = content.replace('icon:', '');
  return WATERMARK_ICONS.find(p => p.id === iconId)?.icon ?? null;
};

const POSITION_OPTIONS: { id: WatermarkPosition; label: string }[] = [
  { id: 'top-left', label: 'Top Left' },
  { id: 'top-right', label: 'Top Right' },
  { id: 'center', label: 'Center' },
  { id: 'bottom-left', label: 'Bottom Left' },
  { id: 'bottom-right', label: 'Bottom Right' },
  { id: 'diagonal', label: 'Diagonal' },
];

// Helper to get position styles for preview
const getPositionStyles = (position: WatermarkPosition, rotation: number): React.CSSProperties => {
  const baseStyle: React.CSSProperties = { position: 'absolute' };

  switch (position) {
    case 'top-left':
      return { ...baseStyle, top: '12px', left: '12px' };
    case 'top-right':
      return { ...baseStyle, top: '12px', right: '12px' };
    case 'center':
      return { ...baseStyle, top: '50%', left: '50%', transform: `translate(-50%, -50%) rotate(${rotation}deg)` };
    case 'bottom-left':
      return { ...baseStyle, bottom: '12px', left: '12px' };
    case 'bottom-right':
      return { ...baseStyle, bottom: '12px', right: '12px' };
    case 'diagonal':
      return { ...baseStyle, top: '50%', left: '50%', transform: `translate(-50%, -50%) rotate(-45deg)` };
    default:
      return { ...baseStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }
};

export function WatermarkPanel() {
  const { settings, updateNestedSettings } = useTemplateDesigner();
  const { watermark } = settings;

  const isIcon = watermark.content?.startsWith('icon:');
  const CurrentIcon = getWatermarkIcon(watermark.content);

  return (
    <div className="space-y-6">
      {/* Enable Toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Droplet className="w-5 h-5 text-primary" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Watermark</Label>
            <p className="text-xs text-muted-foreground">Add background mark to documents</p>
          </div>
        </div>
        <Switch
          checked={watermark.enabled}
          onCheckedChange={(checked) => updateNestedSettings('watermark', { enabled: checked })}
        />
      </div>

      {watermark.enabled && (
        <div className="space-y-6">
          {/* Watermark Icons */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Watermark Icon</Label>
            <div className="grid grid-cols-4 gap-2">
              {WATERMARK_ICONS.map((preset) => {
                const Icon = preset.icon;
                const isSelected = watermark.content === `icon:${preset.id}`;
                return (
                  <button
                    key={preset.id}
                    onClick={() => updateNestedSettings('watermark', { content: `icon:${preset.id}` })}
                    className={`
                      relative aspect-square rounded-lg border-2 flex items-center justify-center transition-all
                      ${isSelected
                        ? 'border-primary bg-primary/5 shadow-md'
                        : 'border-border bg-card hover:bg-accent hover:border-primary/30'
                      }
                    `}
                    title={preset.name}
                  >
                    {isSelected && (
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Position */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Position</Label>
            <div className="grid grid-cols-3 gap-2">
              {POSITION_OPTIONS.map((pos) => {
                const isSelected = watermark.position === pos.id;
                return (
                  <button
                    key={pos.id}
                    onClick={() => updateNestedSettings('watermark', { position: pos.id })}
                    className={`
                      relative aspect-[4/3] rounded-lg border-2 bg-muted/30 transition-all overflow-hidden
                      ${isSelected ? 'border-primary' : 'border-border hover:border-primary/30'}
                    `}
                  >
                    {/* Position indicator dot */}
                    <div
                      className={`absolute w-2.5 h-2.5 rounded-full transition-colors ${isSelected ? 'bg-primary' : 'bg-muted-foreground/40'}`}
                      style={getPositionStyles(pos.id, 0) as React.CSSProperties}
                    />
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        <Check className="w-2.5 h-2.5" />
                      </div>
                    )}
                    <span className="absolute bottom-1.5 left-0 right-0 text-[11px] font-medium text-center text-muted-foreground">
                      {pos.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Opacity */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Opacity</Label>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {watermark.opacity}%
              </span>
            </div>
            <Slider
              value={[watermark.opacity]}
              onValueChange={([value]) => updateNestedSettings('watermark', { opacity: value })}
              min={5}
              max={50}
              step={5}
              className="py-2"
            />
          </div>

          {/* Size */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Size</Label>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {watermark.size}%
              </span>
            </div>
            <Slider
              value={[watermark.size]}
              onValueChange={([value]) => updateNestedSettings('watermark', { size: value })}
              min={20}
              max={100}
              step={10}
              className="py-2"
            />
          </div>

          {/* Rotation */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Rotation</Label>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {watermark.rotation}°
              </span>
            </div>
            <Slider
              value={[watermark.rotation]}
              onValueChange={([value]) => updateNestedSettings('watermark', { rotation: value })}
              min={-90}
              max={90}
              step={15}
              className="py-2"
            />
          </div>

          {/* Live Preview */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Preview</Label>
            <div className="relative h-32 rounded-lg border border-border bg-white overflow-hidden">
              {/* Grid background */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:20px_20px]" />

              {/* Watermark */}
              <div
                className="text-muted-foreground/30 select-none"
                style={{
                  ...getPositionStyles(watermark.position, watermark.rotation),
                  opacity: watermark.opacity / 100,
                }}
              >
                {isIcon && CurrentIcon ? (
                  <CurrentIcon
                    style={{
                      width: `${watermark.size * 0.8}px`,
                      height: `${watermark.size * 0.8}px`,
                      transform: watermark.position !== 'center' && watermark.position !== 'diagonal'
                        ? `rotate(${watermark.rotation}deg)`
                        : undefined,
                    }}
                  />
                ) : (
                  <span
                    className="font-bold whitespace-nowrap"
                    style={{
                      fontSize: `${watermark.size / 4}px`,
                      transform: watermark.position !== 'center' && watermark.position !== 'diagonal'
                        ? `rotate(${watermark.rotation}deg)`
                        : undefined,
                      display: 'inline-block',
                    }}
                  >
                    {watermark.content || 'WATERMARK'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
